import React, { useEffect, useState } from 'react';
import { wizards as wizardsApi, brands as brandsApi } from '@/utils/api';
import {
  Plus, Edit2, Trash2, Copy, ExternalLink, Save, X, FileText, QrCode, Sparkles,
  ChevronUp, ChevronDown, Trash, GripVertical, ArrowLeft,
} from 'lucide-react';
import { toast } from 'sonner';
import { QRCodeCanvas } from 'qrcode.react';

const FIELD_TYPES = [
  { value: 'text', label: 'Short text' },
  { value: 'textarea', label: 'Long text' },
  { value: 'email', label: 'Email' },
  { value: 'tel', label: 'Phone' },
  { value: 'url', label: 'URL / Link' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'select', label: 'Dropdown (select)' },
  { value: 'radio', label: 'Single choice (radio)' },
  { value: 'checkbox', label: 'Single checkbox' },
  { value: 'checkbox_multi', label: 'Multiple choice (checkboxes)' },
  { value: 'file_upload', label: 'File upload (single)' },
  { value: 'file_multi', label: 'File upload (multiple)' },
  { value: 'video_upload', label: 'Video upload' },
  { value: 'vault_list', label: 'Credentials (vault)' },
];

const slugify = (s) =>
  (s || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 40) || `field_${Date.now()}`;

const newField = (labelHint = '') => ({
  id: slugify(labelHint || `field_${Date.now()}`),
  label: labelHint || 'New field',
  type: 'text',
  required: false,
  placeholder: '',
  options: [],
  help_text: '',
});

const newSection = () => ({
  id: `section_${Date.now()}`,
  title: 'New Section',
  description: '',
  skip_for_existing_clients: false,
  fields: [newField('Your answer')],
});

const emptyWizard = (brandId) => ({
  slug: `wizard-${Date.now()}`,
  title: 'New Onboarding Wizard',
  description: '',
  brand_id: brandId || '',
  sections: [
    {
      id: 'your_details',
      title: 'Your details',
      description: 'A quick hello before we dive in.',
      skip_for_existing_clients: false,
      fields: [
        { ...newField('Your name'), id: 'name', type: 'text', required: true },
        { ...newField('Email'), id: 'email', type: 'email', required: true },
        { ...newField('Company'), id: 'company', type: 'text', required: false },
      ],
    },
  ],
  terms_md: '1. Confidentiality applies.\n2. 50% advance required.\n3. 2 rounds of revisions included.',
  allow_signature_draw: true,
  allow_signature_typed: true,
  success_message: "Thanks — we'll be in touch shortly.",
  is_published: false,
});

export default function Wizards() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [brands, setBrands] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [shareUrl, setShareUrl] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiDesc, setAiDesc] = useState('');
  const [aiBusy, setAiBusy] = useState(false);
  const [jsonMode, setJsonMode] = useState(false);

  const reload = () => wizardsApi.list().then(r => setList(r.data)).catch(() => {});
  useEffect(() => {
    reload();
    brandsApi.list().then(r => setBrands(r.data)).catch(() => {});
  }, []);

  const openNew = () => {
    setEditing(emptyWizard(brands[0]?.id || ''));
    setSubmissions([]);
  };

  const openEdit = async (w) => {
    try {
      const r = await wizardsApi.get(w.id);
      setEditing(r.data);
      setJsonMode(false);
      wizardsApi.submissions(w.id).then(rr => setSubmissions(rr.data || [])).catch(() => setSubmissions([]));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load wizard');
    }
  };

  const clone = async (w) => {
    try {
      const r = await wizardsApi.clone(w.id);
      toast.success(`Cloned → ${r.data.title}`);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Clone failed');
    }
  };

  const save = async () => {
    // Basic validation
    if (!editing.title?.trim()) return toast.error('Title is required');
    if (!editing.brand_id) return toast.error('Pick a brand');
    if (!editing.slug?.trim()) return toast.error('Slug is required');
    if (!editing.sections?.length) return toast.error('Add at least one section');
    try {
      if (editing.id) {
        const { id, slug, ...upd } = editing;
        await wizardsApi.update(id, upd);
        toast.success('Saved');
      } else {
        await wizardsApi.create(editing);
        toast.success('Wizard created');
      }
      setEditing(null);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const del = async (id) => {
    if (!window.confirm('Delete wizard? This cannot be undone.')) return;
    await wizardsApi.delete(id);
    reload();
  };

  const runAI = async () => {
    if (!aiDesc.trim() || aiDesc.trim().length < 12) {
      return toast.error('Describe what the wizard should do (at least 12 chars)');
    }
    setAiBusy(true);
    try {
      const r = await wizardsApi.aiGenerate(aiDesc.trim(), brands[0]?.id);
      // Prefill a NEW wizard with the AI output; admin reviews before saving
      setEditing({
        ...emptyWizard(brands[0]?.id || ''),
        title: r.data.title || 'AI-generated wizard',
        description: r.data.description || '',
        sections: (r.data.sections || []).map((s) => ({
          id: s.id || `section_${Math.random().toString(36).slice(2, 8)}`,
          title: s.title || 'Section',
          description: s.description || '',
          skip_for_existing_clients: !!s.skip_for_existing_clients,
          fields: (s.fields || []).map((f) => ({
            id: f.id || slugify(f.label || ''),
            label: f.label || 'Question',
            type: f.type || 'text',
            required: !!f.required,
            placeholder: f.placeholder || '',
            options: Array.isArray(f.options) ? f.options : [],
            help_text: f.help_text || '',
            ...(f.show_if ? { show_if: f.show_if } : {}),
          })),
        })),
        terms_md: r.data.terms_md || '',
        success_message: r.data.success_message || "Thanks — we'll be in touch shortly.",
      });
      setAiOpen(false);
      setAiDesc('');
      toast.success('AI draft ready — review and save');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'AI generation failed');
    } finally {
      setAiBusy(false);
    }
  };

  // ---- Section / field mutators ----
  const patch = (updater) => setEditing((prev) => ({ ...prev, ...updater(prev) }));
  const updSection = (idx, upd) =>
    patch((p) => ({ sections: p.sections.map((s, i) => (i === idx ? { ...s, ...upd } : s)) }));
  const moveSection = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= editing.sections.length) return;
    const arr = [...editing.sections];
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    patch(() => ({ sections: arr }));
  };
  const delSection = (idx) => {
    if (!window.confirm('Delete this section and all its fields?')) return;
    patch((p) => ({ sections: p.sections.filter((_, i) => i !== idx) }));
  };
  const addSection = () => patch((p) => ({ sections: [...p.sections, newSection()] }));

  const updField = (sIdx, fIdx, upd) =>
    updSection(sIdx, {
      fields: editing.sections[sIdx].fields.map((f, i) => (i === fIdx ? { ...f, ...upd } : f)),
    });
  const moveField = (sIdx, fIdx, dir) => {
    const j = fIdx + dir;
    const arr = [...editing.sections[sIdx].fields];
    if (j < 0 || j >= arr.length) return;
    [arr[fIdx], arr[j]] = [arr[j], arr[fIdx]];
    updSection(sIdx, { fields: arr });
  };
  const delField = (sIdx, fIdx) =>
    updSection(sIdx, { fields: editing.sections[sIdx].fields.filter((_, i) => i !== fIdx) });
  const addField = (sIdx) =>
    updSection(sIdx, { fields: [...editing.sections[sIdx].fields, newField()] });

  return (
    <div className="p-6 animate-fade-in" data-testid="wizards-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-black" style={{ color: 'var(--ink)' }}>Onboarding Wizards</h1>
          <p className="text-sm" style={{ color: 'var(--muted)' }}>
            Public multi-step intake forms. Prospects fill → lead appears in your approval queue.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            className="text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-btn font-semibold"
            style={{ background: '#5B2D8E', color: 'white' }}
            onClick={() => setAiOpen(true)}
            data-testid="ai-wizard-button"
          >
            <Sparkles size={13} /> Describe with AI
          </button>
          <button className="btn-primary text-xs flex items-center gap-1.5 px-3 py-1.5" onClick={openNew} data-testid="new-wizard-button">
            <Plus size={13} /> New Wizard
          </button>
        </div>
      </div>

      {list.length === 0 ? (
        <div className="card-base p-10 text-center">
          <FileText size={32} style={{ color: 'var(--border)', margin: '0 auto 12px' }} />
          <p className="text-sm font-medium" style={{ color: 'var(--ink)' }}>No wizards yet</p>
          <p className="text-xs mt-1" style={{ color: 'var(--muted)' }}>Click <b>Describe with AI</b> and tell Claude what you want to collect.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {list.map(w => (
            <div key={w.id} className="card-base p-4" data-testid={`wizard-${w.id}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-bold text-sm" style={{ color: 'var(--ink)' }}>{w.title}</p>
                    <span className="badge-status" style={{ background: w.is_published ? '#3C8A5A15' : '#E8E3D9', color: w.is_published ? '#3C8A5A' : '#5C6B72' }}>
                      {w.is_published ? 'Published' : 'Draft'}
                    </span>
                  </div>
                  <p className="text-xs mt-0.5 font-mono" style={{ color: 'var(--muted)' }}>/w/{w.slug}</p>
                </div>
                <p className="text-xs" style={{ color: 'var(--muted)' }}>
                  {w.submission_count || 0} submissions · {w.sections?.length || 0} steps
                </p>
              </div>
              <div className="flex gap-2 flex-wrap">
                <button onClick={() => openEdit(w)} className="text-xs px-3 py-1.5 rounded-btn font-semibold flex items-center gap-1" style={{ background: '#E6F1F4', color: '#006080' }} data-testid={`edit-wizard-${w.id}`}>
                  <Edit2 size={12} /> Edit
                </button>
                <button onClick={() => clone(w)} className="text-xs px-3 py-1.5 rounded-btn font-semibold flex items-center gap-1" style={{ background: 'var(--bg)', color: 'var(--ink)', border: '1px solid var(--border)' }} data-testid={`clone-wizard-${w.id}`}>
                  <Copy size={12} /> Clone
                </button>
                {w.is_published && (
                  <>
                    <a href={`/w/${w.slug}`} target="_blank" rel="noreferrer" className="text-xs px-3 py-1.5 rounded-btn font-semibold flex items-center gap-1" style={{ background: 'var(--bg)', color: 'var(--muted)', border: '1px solid var(--border)' }}>
                      <ExternalLink size={12} /> Open
                    </a>
                    <button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/w/${w.slug}`); toast.success('Copied'); }} className="text-xs px-3 py-1.5 rounded-btn font-semibold flex items-center gap-1" style={{ background: 'var(--bg)', color: 'var(--muted)', border: '1px solid var(--border)' }}>
                      <Copy size={12} /> Copy link
                    </button>
                    <button onClick={() => setShareUrl(`${window.location.origin}/w/${w.slug}`)} className="text-xs px-3 py-1.5 rounded-btn font-semibold flex items-center gap-1" style={{ background: 'var(--bg)', color: 'var(--muted)', border: '1px solid var(--border)' }} data-testid={`qr-${w.id}`}>
                      <QrCode size={12} /> QR
                    </button>
                  </>
                )}
                <button onClick={() => del(w.id)} className="text-xs p-1.5 ml-auto" style={{ color: '#B34A3A' }} data-testid={`delete-wizard-${w.id}`}>
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ---------- AI generator modal ---------- */}
      {aiOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.55)' }} data-testid="ai-wizard-modal">
          <div className="w-full max-w-lg rounded-card p-5" style={{ background: 'var(--surface)' }}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Sparkles size={16} style={{ color: '#5B2D8E' }} />
                <h2 className="font-black text-lg" style={{ color: 'var(--ink)' }}>Describe your wizard</h2>
              </div>
              <button onClick={() => setAiOpen(false)} style={{ color: 'var(--muted)' }}><X size={18} /></button>
            </div>
            <p className="text-xs mb-3" style={{ color: 'var(--muted)' }}>
              Claude Sonnet 4.5 will turn your description into a ready-to-edit wizard — sections, fields, terms, success message.
              Don't worry about technical details; describe the audience, what you need to collect, and any critical questions.
            </p>
            <textarea
              className="input-agency text-sm resize-none"
              rows={8}
              placeholder="e.g. 30-minute onboarding for SaaS clients who want a Robomotion bot. Collect company info, current stack, data sources, target KPIs, 1-2 demo video uploads, and have them agree to our standard NDA."
              value={aiDesc}
              onChange={(e) => setAiDesc(e.target.value)}
              data-testid="ai-wizard-description"
            />
            <div className="flex gap-2 mt-3">
              <button className="flex-1 py-2 text-sm rounded-btn border font-medium" style={{ borderColor: 'var(--border)', color: 'var(--muted)' }} onClick={() => setAiOpen(false)} disabled={aiBusy}>Cancel</button>
              <button
                className="flex-1 py-2 text-sm rounded-btn font-bold text-white flex items-center justify-center gap-1.5"
                style={{ background: '#5B2D8E' }}
                onClick={runAI}
                disabled={aiBusy || aiDesc.trim().length < 12}
                data-testid="ai-wizard-generate-btn"
              >
                {aiBusy ? 'Drafting…' : (<><Sparkles size={13} /> Generate</>)}
              </button>
            </div>
            <p className="text-[10px] mt-2" style={{ color: 'var(--muted)' }}>
              Needs a Claude API key in Settings → Integrations. Draft is NOT saved automatically — you'll review before publishing.
            </p>
          </div>
        </div>
      )}

      {/* ---------- Editor modal ---------- */}
      {editing && (
        <div className="fixed inset-0 z-40 flex items-start justify-center p-4 overflow-y-auto" style={{ background: 'rgba(0,0,0,0.55)' }} data-testid="wizard-editor-modal">
          <div className="w-full max-w-4xl rounded-card my-6 p-5" style={{ background: 'var(--surface)' }}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <button onClick={() => setEditing(null)} className="text-xs flex items-center gap-1" style={{ color: 'var(--muted)' }}>
                  <ArrowLeft size={14} /> Back
                </button>
                <h2 className="font-black text-lg" style={{ color: 'var(--ink)' }}>{editing.id ? 'Edit wizard' : 'New wizard'}</h2>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setJsonMode(v => !v)}
                  className="text-xs px-2.5 py-1 rounded-btn font-semibold"
                  style={{ background: jsonMode ? '#5B2D8E' : 'var(--bg)', color: jsonMode ? 'white' : 'var(--muted)', border: '1px solid var(--border)' }}
                >
                  {jsonMode ? 'Visual' : 'JSON'}
                </button>
                <button onClick={save} className="btn-primary text-xs flex items-center gap-1 px-3 py-1.5" data-testid="save-wizard-button">
                  <Save size={12} /> Save
                </button>
              </div>
            </div>

            {/* --- top meta --- */}
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Title</label>
                <input className="input-agency text-sm" value={editing.title} onChange={e => setEditing({ ...editing, title: e.target.value })} data-testid="wizard-title-input" />
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Brand</label>
                <select className="input-agency text-sm" value={editing.brand_id || ''} onChange={e => setEditing({ ...editing, brand_id: e.target.value })} data-testid="wizard-brand-select">
                  <option value="">Select brand…</option>
                  {brands.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
            </div>

            <div className="mb-3">
              <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>URL slug</label>
              <div className="flex items-center gap-2">
                <input className="input-agency text-sm font-mono" value={editing.slug || ''} onChange={e => !editing.id && setEditing({ ...editing, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })} disabled={!!editing.id} data-testid="wizard-slug-input" />
                {editing.slug && <span className="text-xs font-mono" style={{ color: 'var(--muted)' }}>→ /w/{editing.slug}</span>}
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Description</label>
              <textarea className="input-agency text-sm resize-none" rows={2} value={editing.description || ''} onChange={e => setEditing({ ...editing, description: e.target.value })} />
            </div>

            {/* --- visual section + field editor OR raw JSON fallback --- */}
            {jsonMode ? (
              <div className="mb-4">
                <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Sections JSON (expert)</label>
                <textarea
                  className="input-agency font-mono resize-none"
                  style={{ fontSize: 11 }}
                  rows={20}
                  value={JSON.stringify(editing.sections || [], null, 2)}
                  onChange={(e) => {
                    try {
                      const parsed = JSON.parse(e.target.value);
                      setEditing({ ...editing, sections: parsed });
                    } catch { /* typing — ignore */ }
                  }}
                  data-testid="wizard-sections-json"
                />
              </div>
            ) : (
              <div className="space-y-3 mb-4">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-bold uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Sections ({editing.sections?.length || 0})</p>
                  <button className="text-xs flex items-center gap-1 px-2.5 py-1 rounded-btn font-semibold" style={{ background: '#E6F1F4', color: '#006080' }} onClick={addSection} data-testid="add-section-button">
                    <Plus size={11} /> Add section
                  </button>
                </div>

                {(editing.sections || []).map((s, sIdx) => (
                  <div key={s.id || sIdx} className="rounded-btn p-3" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }} data-testid={`section-${sIdx}`}>
                    <div className="flex items-start gap-2 mb-3">
                      <GripVertical size={14} style={{ color: 'var(--muted)', marginTop: 6 }} />
                      <div className="flex-1">
                        <input
                          className="input-agency text-sm font-bold"
                          placeholder="Section title"
                          value={s.title}
                          onChange={(e) => updSection(sIdx, { title: e.target.value })}
                          data-testid={`section-title-${sIdx}`}
                        />
                        <input
                          className="input-agency text-xs mt-1.5"
                          placeholder="Optional helper text shown under the title"
                          value={s.description || ''}
                          onChange={(e) => updSection(sIdx, { description: e.target.value })}
                        />
                      </div>
                      <div className="flex flex-col gap-0.5">
                        <button onClick={() => moveSection(sIdx, -1)} disabled={sIdx === 0} className="p-1 rounded" style={{ color: sIdx === 0 ? 'var(--border)' : 'var(--muted)' }} title="Move up">
                          <ChevronUp size={14} />
                        </button>
                        <button onClick={() => moveSection(sIdx, 1)} disabled={sIdx === editing.sections.length - 1} className="p-1 rounded" style={{ color: sIdx === editing.sections.length - 1 ? 'var(--border)' : 'var(--muted)' }} title="Move down">
                          <ChevronDown size={14} />
                        </button>
                      </div>
                      <button onClick={() => delSection(sIdx)} className="p-1" style={{ color: '#B34A3A' }} title="Delete section" data-testid={`delete-section-${sIdx}`}>
                        <Trash size={14} />
                      </button>
                    </div>

                    {/* --- Fields --- */}
                    <div className="space-y-2 pl-4" style={{ borderLeft: '2px solid var(--border)' }}>
                      {(s.fields || []).map((f, fIdx) => (
                        <div key={f.id || fIdx} className="rounded-btn p-2.5" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }} data-testid={`field-${sIdx}-${fIdx}`}>
                          <div className="flex gap-2">
                            <div className="flex-1 grid grid-cols-2 gap-2">
                              <input
                                className="input-agency text-xs"
                                placeholder="Question / label"
                                value={f.label}
                                onChange={(e) => updField(sIdx, fIdx, { label: e.target.value, id: f.id || slugify(e.target.value) })}
                                data-testid={`field-label-${sIdx}-${fIdx}`}
                              />
                              <select
                                className="input-agency text-xs"
                                value={f.type}
                                onChange={(e) => updField(sIdx, fIdx, { type: e.target.value })}
                                data-testid={`field-type-${sIdx}-${fIdx}`}
                              >
                                {FIELD_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                              </select>
                              <input
                                className="input-agency text-xs"
                                placeholder="Placeholder (optional)"
                                value={f.placeholder || ''}
                                onChange={(e) => updField(sIdx, fIdx, { placeholder: e.target.value })}
                              />
                              <input
                                className="input-agency text-xs"
                                placeholder="Help text (optional)"
                                value={f.help_text || ''}
                                onChange={(e) => updField(sIdx, fIdx, { help_text: e.target.value })}
                              />
                              {['select', 'radio', 'checkbox_multi'].includes(f.type) && (
                                <input
                                  className="input-agency text-xs col-span-2"
                                  placeholder="Options, comma-separated (e.g. Small, Medium, Large)"
                                  value={(f.options || []).join(', ')}
                                  onChange={(e) => updField(sIdx, fIdx, { options: e.target.value.split(',').map(x => x.trim()).filter(Boolean) })}
                                  data-testid={`field-options-${sIdx}-${fIdx}`}
                                />
                              )}
                              <label className="flex items-center gap-1.5 text-xs col-span-2" style={{ color: 'var(--ink)' }}>
                                <input
                                  type="checkbox"
                                  checked={!!f.required}
                                  onChange={(e) => updField(sIdx, fIdx, { required: e.target.checked })}
                                  data-testid={`field-required-${sIdx}-${fIdx}`}
                                />
                                Required
                                <span className="font-mono text-[10px] ml-auto" style={{ color: 'var(--muted)' }}>id: {f.id}</span>
                              </label>
                            </div>
                            <div className="flex flex-col gap-0.5 flex-shrink-0">
                              <button onClick={() => moveField(sIdx, fIdx, -1)} disabled={fIdx === 0} className="p-1" style={{ color: fIdx === 0 ? 'var(--border)' : 'var(--muted)' }}>
                                <ChevronUp size={12} />
                              </button>
                              <button onClick={() => moveField(sIdx, fIdx, 1)} disabled={fIdx === s.fields.length - 1} className="p-1" style={{ color: fIdx === s.fields.length - 1 ? 'var(--border)' : 'var(--muted)' }}>
                                <ChevronDown size={12} />
                              </button>
                              <button onClick={() => delField(sIdx, fIdx)} className="p-1" style={{ color: '#B34A3A' }} data-testid={`delete-field-${sIdx}-${fIdx}`}>
                                <Trash size={12} />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                      <button
                        onClick={() => addField(sIdx)}
                        className="text-xs flex items-center gap-1 px-2 py-1 rounded-btn font-semibold"
                        style={{ background: 'transparent', color: '#006080' }}
                        data-testid={`add-field-${sIdx}`}
                      >
                        <Plus size={11} /> Add field
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* --- terms, success, flags --- */}
            <div className="mb-3">
              <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Terms (shown on final step, Markdown-ish)</label>
              <textarea className="input-agency text-sm resize-none" rows={5} value={editing.terms_md || ''} onChange={e => setEditing({ ...editing, terms_md: e.target.value })} />
            </div>
            <div className="mb-3">
              <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Success message</label>
              <input className="input-agency text-sm" value={editing.success_message || ''} onChange={e => setEditing({ ...editing, success_message: e.target.value })} />
            </div>
            <div className="grid grid-cols-3 gap-3 mb-4">
              <label className="flex items-center gap-2 text-sm" style={{ color: 'var(--ink)' }}>
                <input type="checkbox" checked={!!editing.allow_signature_draw} onChange={e => setEditing({ ...editing, allow_signature_draw: e.target.checked })} /> Allow drawn sig
              </label>
              <label className="flex items-center gap-2 text-sm" style={{ color: 'var(--ink)' }}>
                <input type="checkbox" checked={!!editing.allow_signature_typed} onChange={e => setEditing({ ...editing, allow_signature_typed: e.target.checked })} /> Allow typed sig
              </label>
              <label className="flex items-center gap-2 text-sm" style={{ color: 'var(--ink)' }}>
                <input type="checkbox" checked={!!editing.is_published} onChange={e => setEditing({ ...editing, is_published: e.target.checked })} data-testid="wizard-published-toggle" /> Published
              </label>
            </div>

            {editing.id && submissions.length > 0 && (
              <div className="mt-3 pt-3" style={{ borderTop: '1px solid var(--border)' }}>
                <p className="text-xs font-bold uppercase tracking-wide mb-2" style={{ color: 'var(--muted)' }}>
                  Recent submissions ({submissions.length})
                </p>
                <div className="space-y-1">
                  {submissions.slice(0, 5).map(s => (
                    <div key={s.id} className="text-xs flex items-center justify-between" style={{ color: 'var(--muted)' }}>
                      <span>{s.name || s.email || 'Submission'} · {new Date(s.submitted_at || s.saved_at).toLocaleString()}</span>
                      <span className="badge-status" style={{ background: s.status === 'submitted' ? '#3C8A5A15' : '#D9A44115', color: s.status === 'submitted' ? '#3C8A5A' : '#D9A441' }}>{s.status}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ---------- QR modal ---------- */}
      {shareUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.6)' }} onClick={() => setShareUrl(null)} data-testid="qr-modal">
          <div className="card-base p-6 max-w-sm w-full text-center" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-black text-sm" style={{ color: 'var(--ink)' }}>Scan to open wizard</h3>
              <button onClick={() => setShareUrl(null)} style={{ color: 'var(--muted)' }}><X size={16} /></button>
            </div>
            <div className="bg-white p-4 rounded-card flex justify-center mb-3">
              <QRCodeCanvas value={shareUrl} size={220} level="M" includeMargin />
            </div>
            <p className="text-xs font-mono break-all mb-3" style={{ color: 'var(--muted)' }}>{shareUrl}</p>
            <div className="flex gap-2">
              <button onClick={() => { navigator.clipboard.writeText(shareUrl); toast.success('Copied'); }} className="flex-1 py-2 text-xs rounded-btn font-semibold" style={{ background: 'var(--bg)', color: 'var(--ink)', border: '1px solid var(--border)' }}>
                <Copy size={11} className="inline mr-1" /> Copy
              </button>
              <a href={shareUrl} target="_blank" rel="noreferrer" className="flex-1 py-2 text-xs rounded-btn font-semibold text-white" style={{ background: '#006080' }}>
                Open
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
